import React, { useRef, useState, useLayoutEffect } from 'react';
import { RestrictedPdfCard, UnsupportedFormatCard, FileTooLargeCard, ProcessingFailedCard } from '../ui/ErrorCard';

// Letter-size aspect ratio (height / width)
const LETTER_ASPECT_RATIO = 11 / 8.5;
const MIN_PDF_HEIGHT = 200 * LETTER_ASPECT_RATIO;
const PADDING = 40;

// BASE SIZE: All content is designed at this size, then scaled
const BASE_HEIGHT = 792; // Standard letter height in points at 72 DPI (11 inches)
const BASE_WIDTH = BASE_HEIGHT / LETTER_ASPECT_RATIO;

/**
 * Empty state for PDF Viewer.
 * Error state uses named ErrorCard variants based on errorType.
 * Normal empty state renders the familiar dashed PDF outline.
 */
const ViewerEmptyState = ({
  hasDocuments = false,
  isError = false,
  errorType = null,
  errorSubtype = null,
  fileInfo = null,
  onReplace,
  onDelete,
}) => {
  const emptyStateContainerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [displayHeight, setDisplayHeight] = useState(MIN_PDF_HEIGHT);

  const calculateHeight = (containerWidth, containerHeight) => {
    if (!containerWidth || !containerHeight) return MIN_PDF_HEIGHT;
    const availableWidth = containerWidth - PADDING;
    const availableHeight = containerHeight - PADDING;
    const heightFromWidth = availableWidth * LETTER_ASPECT_RATIO;
    const optimalHeight = Math.min(heightFromWidth, availableHeight);
    return Math.max(optimalHeight, MIN_PDF_HEIGHT);
  };

  useLayoutEffect(() => {
    const container = emptyStateContainerRef.current?.parentElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDisplayHeight(calculateHeight(rect.width, rect.height));
    }

    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const newHeight = calculateHeight(width, height);
        setDisplayHeight((prev) => Math.abs(newHeight - prev) > 5 ? newHeight : prev);
      }
    });

    resizeObserverRef.current.observe(container);
    return () => resizeObserverRef.current?.disconnect();
  }, []);

  if (isError) {
    const cardProps = { file: fileInfo, onReplace, onDelete };
    let card;
    switch (errorType) {
      case 'restricted_pdf':
        card = <RestrictedPdfCard {...cardProps} />;
        break;
      case 'unsupported_format':
        card = <UnsupportedFormatCard {...cardProps} errorSubtype={errorSubtype} />;
        break;
      case 'file_too_large':
        card = <FileTooLargeCard {...cardProps} />;
        break;
      default:
        card = <ProcessingFailedCard {...cardProps} />;
    }

    return (
      <div
        ref={emptyStateContainerRef}
        className="h-full flex items-center justify-center"
        style={{ padding: `${PADDING / 2}px` }}
      >
        <div style={{ width: '100%', maxWidth: `${BASE_WIDTH}px` }}>
          {card}
        </div>
      </div>
    );
  }

  const scale = displayHeight / BASE_HEIGHT;
  const BASE_STAMP_WIDTH = (1.33 / 8.5) * BASE_WIDTH;
  const BASE_STAMP_HEIGHT = (0.75 / 11) * BASE_HEIGHT;
  const BASE_STAMP_PADDING = 20;

  return (
    <div ref={emptyStateContainerRef} className="h-full flex pt-5 items-start justify-center">
      <div style={{ width: `${BASE_WIDTH * scale}px`, height: `${BASE_HEIGHT * scale}px` }}>
        <div
          style={{
            width: `${BASE_WIDTH}px`,
            height: `${BASE_HEIGHT}px`,
            transform: `scale(${scale})`,
            transformOrigin: '0 0'
          }}
        >
          <div className="relative">
            <div
              className="bg-white border-[3px] border-dashed border-app-gray-600 flex flex-col items-center justify-start p-[72px]"
              style={{ width: `${BASE_WIDTH}px`, height: `${BASE_HEIGHT}px` }}
            >
              <h3 className="text-4xl text-app-gray-900 text-left mb-4 self-start">
                No documents yet!
              </h3>
              <p className="text-app-md text-app-gray-500 text-left self-start">
                {hasDocuments
                  ? 'Try selecting a document from the document table.'
                  : 'Upload your first document to the document table to see it displayed here.'}
              </p>
            </div>

            <div
              className="absolute bg-yellow-300/25 border-8 border-yellow-500/25"
              style={{
                width: `${BASE_STAMP_WIDTH}px`,
                height: `${BASE_STAMP_HEIGHT}px`,
                bottom: `${BASE_STAMP_PADDING}px`,
                right: `${BASE_STAMP_PADDING}px`,
                borderRadius: '6px'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewerEmptyState;
